import React, { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { logout, selectAccountId, selectAuthenticatedUserToken } from '../../store/auth/authSlice'
import { Token } from '../../common/token'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useUTMHandler } from '../../common/utm'
import { MFAModal } from './MFAModal'
import './MFALogin.scss'
import { ReactComponent as ZocksLogoFullBlack } from '../../assets/ZocksLogoFullBlack.svg'
import { useLazyListFactorsQuery } from '../../store/mfa/mfa'
import { MFAFactor } from '../../models/MFA'
import { resetFlow, setupMFAForLogin, toClaimCodePage } from '../../store/mfa/mfaSlice'
import { Button } from '../../components/Button/Button'

const LoginHeader: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  return (
    <div className='auth-header'>
      <Button
        onClick={() => {
          dispatch(logout(location))
          navigate('/mfa')
        }}
      >
        <ZocksLogoFullBlack className='auth-logo' />
      </Button>
    </div>
  )
}

const getDefaultFactor = (factors: MFAFactor[]): MFAFactor => {
  return factors.find((factor) => factor.default) ?? factors[0]
}

const calculateTokenExpirationTimeLeft = (tokenExpiration: number): number => {
  const now = new Date().getTime()
  return Math.max(tokenExpiration * 1000 - now, 0)
}

export const MFALogin: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const accountId = useAppSelector(selectAccountId)
  const authenticatedUserToken = useAppSelector(selectAuthenticatedUserToken)
  const { appendUTMParams } = useUTMHandler()
  const [showMFAModal, setShowMFAModal] = useState(true)
  const [loading, setLoading] = useState<boolean>(true)
  const dispatch = useAppDispatch()

  const [listFactors] = useLazyListFactorsQuery()

  useEffect(() => {
    if (authenticatedUserToken) {
      const token = Token.fromSerialized(authenticatedUserToken)
      const tokenExpirationTimeout = setTimeout(() => {
        dispatch(logout(location))
        navigate('/mfa')
      }, calculateTokenExpirationTimeLeft(token.accessTokenExpiration))
      return () => {
        clearTimeout(tokenExpirationTimeout)
      }
    }
  }, [authenticatedUserToken])

  useEffect(() => {
    dispatch(resetFlow())
    setLoading(true)
    listFactors({ accountId }).then(({ data: factors }) => {
      try {
        if (factors) {
          if (factors.length > 0) {
            dispatch(toClaimCodePage(getDefaultFactor(factors)))
          } else {
            dispatch(setupMFAForLogin())
          }
        }
      } finally {
        setLoading(false)
      }
    })
  }, [])

  if (authenticatedUserToken) {
    const token = Token.fromSerialized(authenticatedUserToken)
    if (token.tokenUse !== 'mfa') {
      return <Navigate to={appendUTMParams('/', true)} replace />
    }
  }

  const handleHideMFAModal = () => {
    setShowMFAModal(false)
  }

  return (
    <>
      <LoginHeader />
      <MFAModal show={showMFAModal} onHide={handleHideMFAModal} login={true} loading={loading} />
    </>
  )
}
